import React, { useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { pageLoadingActive } from "../../../components/Background"
import { requestMemberInformation } from "../../../requests/members_requests"
import { ContentContext } from "../../../contexts/ContentContext"
import { BreadCrumbsGeneral } from "../../../components/NavigationComponents"
import { useTranslation } from "react-i18next"
import { BanHistory } from "../../../components/BanComponents"
import { AddtoChatCheckbox, MemberCharacterComponent, MemberProfileComponent, PaginationComponent, TicketButton } from "../../../components/DeveloperComponents"
import { formatTimestamp } from "../../../utils"
import { CategoryBubble, PriorityBubble, StatusBubble } from "../../../components/TicketComponents"
import { requestChatIds } from "../../../requests/chat_requests"
import { AccountContext } from "../../../contexts/AccountContexts"
import { devPageIndices, rowsPerPage, tableSortingEntries } from "../../../entries"
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io"
import { PageHeader } from "../../../components/HeaderComponents"


export const MemberProfilePage = () => {
    const { t } = useTranslation('common')
    const { user, devs } = useContext(AccountContext)
    const { servers, openErrorModal } = useContext(ContentContext)

    const [member, setMember] = useState(null)
    const [characters, setCharacters] = useState(null)
    const [tickets, setTickets] = useState([])
    const [activeChatIds, setActiveChatIds] = useState([])

    const { member_id } = useParams()

    const [entriesPerPage, setEntriesPerPage] = useState(localStorage.getItem(user.id + "_perPageMemberTickets") ? parseInt(localStorage.getItem(user.id + "_perPageMemberTickets")) : 25);
    const [currentEntries, setCurrentEntries] = useState([])
    const [indexOfFirstEntry, setIndexOfFirstEntry] = useState(0)
    const [indexOfLastEntry, setIndexOfLastEntry] = useState(0)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });

    const sortedData = React.useMemo(() => {
        let sortableData = [...tickets];


        if (sortConfig.key !== null) {
            sortableData.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Handle undefined values
                if (aValue === undefined || aValue === "") return sortConfig.direction === "asc" ? 1 : -1;
                if (bValue === undefined || bValue === "") return sortConfig.direction === "asc" ? -1 : 1;

                // Handle different types
                const isNumber = (val) => typeof val === 'number';
                const isDate = (val) => !isNaN(Date.parse(val));

                // Case-insensitive string comparison
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const aString = aValue.toLowerCase();
                    const bString = bValue.toLowerCase();
                    if (aString < bString) return sortConfig.direction === "asc" ? -1 : 1;
                    if (aString > bString) return sortConfig.direction === "asc" ? 1 : -1;
                }

                // Number comparison
                if (isNumber(aValue) && isNumber(bValue)) {
                    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
                }

                // Date comparison
                if (isDate(aValue) && isDate(bValue)) {
                    const aDate = new Date(aValue).getTime();
                    const bDate = new Date(bValue).getTime();
                    return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
                }

                return 0;
            });
        }
        return sortableData;
    }, [tickets, sortConfig]);


    useEffect(() => {
        let _currentEntries = sortedData
            .slice(indexOfFirstEntry, indexOfLastEntry); // Entries for the current page
        setCurrentEntries(_currentEntries)

    }, [sortedData, indexOfFirstEntry, indexOfLastEntry])


    useEffect(() => {
        if (member_id && Object.keys(servers).length > 0) {
            pageLoadingActive(true)
            requestMemberInformation({ memberId: member_id })
                .then((data) => {
                    setMember(data.member)
                    let serverToCharacter_ = {}
                    let serverData = Object.values(servers)
                    let serverNameToServer = {}
                    for (let i = 0; i < Object.keys(servers).length; i++) {
                        serverToCharacter_[serverData[i].id] = []
                        serverNameToServer[serverData[i].title.toLowerCase()] = serverData[i]
                    }
                    for (let i = 0; i < data.member.characters.length; i++) {
                        let character = data.member.characters[i]
                        let charServer = serverNameToServer[character.server.toLowerCase()]
                        serverToCharacter_[charServer.id].push(character)
                    }
                    setCharacters(serverToCharacter_)

                    setTickets([...data.tickets])
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
                .finally(() => {
                    pageLoadingActive(false)
                })
        }

    }, [member_id, servers])

    useEffect(() => {
        if (tickets?.length > 0) {
            requestChatIds({})
                .then((data) => {
                    setActiveChatIds(data.active_chat_ids)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        }
    }, [tickets])


    const handlePerPage = (event) => {
        localStorage.setItem(user.id + "_perPageMemberTickets", event.target.value)
        // if (event.target.value !== entriesPerPage) {
        //     setCurrentPage(1)
        // }
        setEntriesPerPage(event.target.value)
    }


    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        if (sortConfig.key === key && sortConfig.direction === "desc") {
            key = null
        }
        if (sortConfig.key !== key || sortConfig.direction !== direction) {
            // setCurrentPage(1)
        }

        setSortConfig({ key, direction });
    };

    const getArrow = (key) => {
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") {
                return <IoMdArrowDropup size={16} className="inline ml-1" />;
            } else {
                return <IoMdArrowDropdown size={16} className="inline ml-1" />;
            }
        }
        return null;
    };

    if (!member) {
        return <></>
    }

    const breadCrumbs = [
        {
            link: "/admin/dashboard",
            name: t("Home")
        },
        {
            link: "/admin/members",
            name: t("Players", { ns: "sidebar" })
        },
        {
            link: null,
            name: member?.name
        }
    ]

    return (
        <>
            <div className="flex flex-col justify-start items-center w-full py-2 gap-5 pb-10 px-4 h-full">
                <PageHeader pageTitle={`${t("Players", { ns: "sidebar" })} - ${member?.name}`} />
                <div className=" flex w-full  justify-between lg:grid lg:grid-cols-3">
                    <div className="flex flex-col flex-1">
                        <h1 className="text-xl font-semibold">{t('Players', { ns: "sidebar" })} - {member.name}</h1>
                        <BreadCrumbsGeneral elements={breadCrumbs} />
                    </div>
                </div>

                <div className="flex flex-col w-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-lg gap-3 py-5">
                    <div className="flex w-full flex-wrap lg:grid lg:grid-cols-3 ">
                        <div className="flex flex-col gap-3 px-5">
                            <div className={`flex flex-col ${member?.banned ? "text-red-600 dark:text-red-500" : ""}`}>
                                <span className="font-semibold text-lg">{member.name}</span>
                                <span className="text-sm font-semibold">{member.email}</span>
                            </div>
                            {
                                member.banned &&
                                <span className="text-red-600 text-lg font-bold">{t("Banned", { ns: "edit" })}</span>
                            }
                            {
                                member &&
                                <BanHistory ticketInfo={member} ticketUser={member} />
                            }

                        </div>
                        <div className="flex flex-col gap-2">
                            {
                                characters &&
                                <span className="font-semibold">{t("Player's Characters")}</span>
                            }
                            <div className="flex gap-6">

                                {
                                    characters &&
                                    Object.values(servers).sort((a, b) => a.title - b.title).map((server) =>
                                        <div className="flex flex-col gap-3 min-w-32">
                                            <span className="w-20 font-semibold">{server.title}</span>
                                            {

                                                characters[server.id]?.map((character) =>
                                                    // <span className="bg-slate-200 dark:bg-slate-600 rounded-md text-center p-0.5">{character.characterName}</span>
                                                    <div className="join">
                                                        <span className="join-item bg-slate-200 dark:bg-slate-600 rounded-md text-center px-3 py-0.5">{character.characterName} </span>
                                                        {
                                                            character?.characterLevel &&
                                                        <span className="join-item bg-stone-200 dark:bg-slate-800 rounded-md text-center w-10 p-1 text-sm font-semibold">{character?.characterLevel}</span>
                                                        }
                                                    </div>
                                                )
                                            }
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        <div className="mx-auto flex flex-col font-semibold gap-1">
                            <span>{t("Tickets")}</span>
                            <div className="flex gap-2 items-center">
                                <span className="min-w-40">{t("ACTIVE")}</span>
                                <span className="">{member.n_active_tickets}</span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <span className="min-w-40 text-green-600">{t("RESOLVED")}</span>
                                <span className="">{member.n_resolved_tickets}</span>
                            </div>
                            <div className="flex gap-2 items-center">
                                <span className="min-w-40 text-red-600">{t("CLOSED")}</span>
                                <span className="">{member.n_closed_tickets}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <main className="flex flex-col items-center w-full mx-auto  bg-white dark:bg-gray-700 rounded-xl shadow-sm  gap-4 h-full">
                    <div className="flex w-full mt-4 px-4 gap-2 items-center justify-between">
                        <div className="flex gap-4 items-center">
                            <div className="flex gap-2 items-center">

                                <select required className=" select select-sm h-10 select-bordered w-[70px] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600" value={entriesPerPage} onChange={handlePerPage}>
                                    {rowsPerPage.map((i, index) => (<option key={index} value={i} >{i}</option>))}
                                </select>
                                <p>{t('entries per page')}</p>
                            </div>
                        </div>
                        <div className="">
                            <p>{tickets.length} {t('entries')}</p>
                        </div>
                    </div>
                    <table className="table  w-full" >
                        <thead className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300">
                            <tr className="border-gray-300 dark:border-gray-600">
                                {Object.keys(devPageIndices).map((el, index) => (
                                    <th className="max-w-xs truncate cursor-pointer select-none" key={index}
                                        onClick={() => { tableSortingEntries[index] && handleSort(tableSortingEntries[index]) }}
                                    >
                                        <div className="flex items-center">
                                            {t(el)} {tableSortingEntries[index] && getArrow(tableSortingEntries[index])}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                currentEntries
                                    .map((ticket, index) =>
                                        <tr key={index} className="h-20 border-gray-300 dark:border-gray-600">
                                            <td className="max-w-xs relative"><TicketButton ticketItems={ticket} /></td>
                                            <td className="max-w-xs truncate"><AddtoChatCheckbox ticketId={ticket.id} isActive={activeChatIds.includes(String(ticket.id)) || activeChatIds.includes(ticket.id)} /></td>
                                            <td className={`max-w-xs truncate`}><MemberProfileComponent ticket={ticket} member={member} /></td>
                                            <td className="max-w-xs truncate"><MemberCharacterComponent ticket={ticket} /></td>
                                            <td className={`max-w-xs truncate ${ticket.banned && "text-red-600"}`}>{ticket.email}</td>
                                            <td className="max-w-xs truncate">{servers[ticket.server].title}</td>
                                            <td className="max-w-xs truncate"><CategoryBubble ticketCategory={ticket.category} /> </td>
                                            <td className="max-w-xs truncate"><StatusBubble ticketStatus={ticket.status} /></td>
                                            <td className="max-w-xs truncate"><PriorityBubble ticketPriority={ticket.priority ? ticket.priority : "Normal"} /></td>
                                            <td className="max-w-xs truncate">{formatTimestamp(ticket.lastUpdated * 1000)}</td>
                                            <td className="max-w-xs truncate">{formatTimestamp(ticket.createdAt * 1000)}</td>
                                            <td className="max-w-xs truncate">
                                                {ticket.assignedTo ? devs[ticket.assignedTo].name : "-"}
                                            </td>
                                        </tr>
                                    )
                            }
                        </tbody>
                    </table>
                    {
                        currentEntries?.length === 0 && <span className="italic">{t("No entries found")}</span>
                    }
                    <PaginationComponent data={tickets} sortedData={sortedData} entriesPerPage={entriesPerPage} setIndexOfFirstEntry={setIndexOfFirstEntry} setIndexOfLastEntry={setIndexOfLastEntry} />

                </main>
            </div>
        </>
    )
}